import { type SizeProps, sizeDefault } from "@/utils/constants/sizeDefault";
import { type ThreeProps, threeDefault } from "@/utils/constants/threeDefault";
import { CANVAS_TREATMENTS } from "@/utils/content/canvasLabels";
import { HDALU_TREATMENTS } from "@/utils/content/hdaluLabels";
import {
  PRODUCT_TYPE,
  PRODUCT_TYPE_PROPS,
  ProductOptions,
  ProductType,
} from "@/utils/content/productTypes";
import { SIZE_OPTIONS, SIZE_OPTIONS_HD } from "@/utils/content/sizeLabels";
import { DeepPartial, ImageCrop } from "@/utils/types/types";
import { computed, map } from "nanostores";

export type VisualizerStoreProps = {
  currPricing: string;
  selectedProduct: ProductType;
  defaultSize: SizeProps;
  selectedSize: SizeProps;
  selectedRenderView: string;
  threeGlobals: ThreeProps;
  showDims: boolean;
  showGuide: boolean;
  showLoading: boolean;
  threeContainer: HTMLElement | null;
  threeCanvas: HTMLElement | null;
  forceUpdate: number;
  zoom: number;
  mode3D: boolean;
  recentActions: string[];
  options: Partial<ProductOptions>;
  imageCrop: Partial<ImageCrop>;
  cropActive: boolean;
  shouldUploadCrop: boolean;
  roomView: string;
  activeStep: {
    [PRODUCT_TYPE.GICLEE]: string;
    [PRODUCT_TYPE.CANVAS]: string;
    [PRODUCT_TYPE.HD_ALUMINUM]: string;
  };
  _lastTreatmentContext?: any;
  editingItemId?: string | null;
  /** Staff: customer already bought the print — charge framing only (see stores/prevPrinted.ts). */
  prevPrinted?: boolean;
  prevPrintedOrder?: string;
  /** Staff: customer-supplied art, no digital file — priced as a Tier-1 print + framing, labelled "supplied" (see stores/prevPrinted.ts). */
  suppliedArt?: boolean;
  /** Staff: what the supplied piece is ("Cora Smith 16x20") — becomes the line's file name (09-22). */
  suppliedTitle?: string;
  /** Staff: multi-window mat on the placeholder — size = glass, no print, ticked cart lines = windows (stores/multiWindow.ts). */
  multiWindow?: boolean;
  multiWindowLineIds?: string[];
  /** The mat width before the box was ticked (the box sets it to 0) — put back on untick. */
  multiWindowPrevMat?: number;
  /** Specs were seeded from a cart line ("Use these specs") — option panels restore like edit mode. */
  seededSpecs?: boolean;
  seededLabel?: string;
  /** Seeded specs + an image larger than the seeded surface/stretcher allows: the size the Sizes panel auto-picked (e.g. `24" x 36"`), for the strip. */
  seededSizeFit?: string;
};

// ─── In-flight crop upload (2026-09-09) ──────────────────────────────────────
// The crop is uploaded asynchronously when the customer leaves the Sizes step
// (or collapses the panel). ADD TO CART could run before that upload
// resolved, snapshot the store, and add the item with no croppedImageUrl —
// reproduced on staging 09-09 (order 26790). Cart add/update await this.
// Timing: the upload starts from a 150 ms debounce in Cropper.canvasPreview;
// addLineItem/updateItem sleep ≥ 700 ms and upload the preview before they
// call waitForCropUpload, so the tracker is always set by then.
let pendingCropUpload: Promise<void> | null = null;
export const trackCropUpload = <T>(p: Promise<T>): Promise<T> => {
  // Tracked copy never rejects (the caller handles the real failure) so an
  // un-awaited copy can't raise a second unhandled rejection.
  const tracked: Promise<void> = p.then(
    () => undefined,
    () => undefined
  );
  pendingCropUpload = tracked;
  tracked.then(() => {
    if (pendingCropUpload === tracked) pendingCropUpload = null;
  });
  return p;
};
/** Resolves when no crop upload is in flight (or after maxWaitMs). */
export const waitForCropUpload = async (maxWaitMs = 20000) => {
  if (!pendingCropUpload) return;
  await Promise.race([
    pendingCropUpload,
    new Promise(resolve => setTimeout(resolve, maxWaitMs)),
  ]);
};

export const $visualizer = map<VisualizerStoreProps>({
  currPricing: "0",
  prevPrinted: false,
  prevPrintedOrder: "",
  suppliedArt: false,
  suppliedTitle: "",
  multiWindow: false,
  multiWindowLineIds: [],
  cropActive: false,
  shouldUploadCrop: false,
  forceUpdate: 0,
  imageCrop: {},
  recentActions: [],
  roomView: "cntRoom0",
  options: PRODUCT_TYPE_PROPS[PRODUCT_TYPE.GICLEE].options,
  selectedProduct: PRODUCT_TYPE.GICLEE,
  selectedRenderView: "Straight",
  defaultSize: sizeDefault,
  selectedSize: sizeDefault,
  showDims: true,
  showGuide: false,
  showLoading: false,
  threeCanvas: null,
  threeContainer: null,
  threeGlobals: threeDefault,
  zoom: 0,
  mode3D: false,
  activeStep: {
    [PRODUCT_TYPE.GICLEE]: "0",
    [PRODUCT_TYPE.CANVAS]: "0",
    [PRODUCT_TYPE.HD_ALUMINUM]: "0",
  },
  // activeCanvasPanel: "0",
  // activeAluPanel: "0",
  // activeGicleePanel: "0",
  editingItemId: null,
});

export const $visualizerComputed = computed($visualizer, visualizer => {
  const readyToHang =
    visualizer.options.treatmentOptions?.type === HDALU_TREATMENTS[0] ||
    visualizer.options.treatmentOptions?.type === CANVAS_TREATMENTS[0];

  return {
    readyToHang,
  };
});

const cachedStorePartials: Partial<Record<ProductType, VisualizerStoreProps>> =
  {};
const cahcedKeys: (keyof VisualizerStoreProps)[] = [
  "currPricing",
  "cropActive",
  "imageCrop",
  "roomView",
  "options",
  "selectedRenderView",
  "selectedSize",
  "showDims",
  "zoom",
  "mode3D",
];

export const visualizerService = {
  selectProduct(product: ProductType) {
    const store = $visualizer.get();
    const currentStore = structuredClone(
      Object.fromEntries(cahcedKeys.map(key => [key, store[key]]))
    ) as any;
    cachedStorePartials[store.selectedProduct] = currentStore;
    const recoveredStore: Partial<VisualizerStoreProps> = cachedStorePartials[
      product
    ] || { options: PRODUCT_TYPE_PROPS[product].options };

    const allowedSizeTypes =
      product === PRODUCT_TYPE.HD_ALUMINUM ? SIZE_OPTIONS_HD : SIZE_OPTIONS;
    const isAllowedSizeType = allowedSizeTypes.includes(
      store.selectedSize.type
    );
    if (!isAllowedSizeType) {
      store.selectedSize = { ...store.selectedSize, type: allowedSizeTypes[0] };
    }

    const newStore: VisualizerStoreProps = {
      ...store,
      ...recoveredStore,
      selectedProduct: product,
    };

    $visualizer.set(newStore);
  },
  setImageCrop(crop?: Partial<ImageCrop>) {
    const store = $visualizer.get();
    if (crop) {
      $visualizer.setKey("imageCrop", {
        pixelHeightCropped: crop?.pixelHeightCropped
          ? crop.pixelHeightCropped
          : store.imageCrop.pixelHeightCropped,
        pixelWidthCropped: crop?.pixelWidthCropped
          ? crop.pixelWidthCropped
          : store.imageCrop.pixelWidthCropped,
        realHeightCropped: crop?.realHeightCropped
          ? crop.realHeightCropped
          : store.imageCrop.realHeightCropped,
        realWidthCropped: crop?.realWidthCropped
          ? crop.realWidthCropped
          : store.imageCrop.realWidthCropped,
        url: crop?.url ? crop.url : store.imageCrop.url,
        base64: crop?.base64 ? crop.base64 : store.imageCrop.base64,
        // Full-res crop rectangle (fractions) — travels with the url so the
        // order build can crop the RAW. See CropRect in utils/types/types.ts.
        rect: crop?.rect ? crop.rect : store.imageCrop.rect,
      });
    } else {
      $visualizer.setKey("imageCrop", {
        pixelHeightCropped: undefined,
        pixelWidthCropped: undefined,
        realHeightCropped: undefined,
        realWidthCropped: undefined,
        url: undefined,
        rect: undefined,
      });
    }
  },
  resetSize() {
    const store = $visualizer.get();

    $visualizer.setKey("selectedSize", {
      ...store.selectedSize,
      dimensions: store.defaultSize.dimensions,
      locked: true,
      orientation: store.defaultSize.orientation,
    });
  },
  updateSize(size: DeepPartial<SizeProps>) {
    const newSize = { ...size } as SizeProps;
    const store = $visualizer.get();

    if (newSize.dimensions) {
      newSize.dimensions = {
        ...newSize.dimensions,
        sqft: (newSize.dimensions.width * newSize.dimensions.height) / 144,
      };
      newSize.orientation =
        newSize.dimensions.width >= newSize.dimensions.height
          ? "Landscape"
          : "Portrait";
    }

    $visualizer.setKey("selectedSize", {
      ...store.selectedSize,
      ...newSize,
    });
  },
};
